import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import { environment } from '@env/environment';
import { UpdateProfileDto, User, UserProfile } from '@core/models/user';
import { IResponse } from '@core/models/general';

export type DniField = 'dniFrontal' | 'dniReverso';

export interface CheckDniResult {
  documentNumber: string;
  firstName: string;
  fatherSurname: string;
  motherSurname: string;
  address: string;
}

export interface DocumentValidationMetadata {
  side: 'front' | 'reverse';
  outcome: 'approved' | 'manual_review' | 'retry' | 'rejected';
  reason: string;
  attemptsUsed: number;
  remainingAttempts: number;
  maxAttempts: number;
}

export interface DniUploadResult {
  data: UserProfile;
  message: string;
  validation: DocumentValidationMetadata;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly http = inject(HttpClient);

  readonly url = `${environment.api.baseUrl}/users`;

  me(): Observable<User> {
    return this.http.get<IResponse<User>>(`${this.url}/me`).pipe(map(response => response.data));
  }

  updateProfile(dto: UpdateProfileDto): Observable<UserProfile> {
    return this.http
      .patch<IResponse<UserProfile>>(`${this.url}/profile`, dto)
      .pipe(map(response => response.data));
  }

  uploadDni(field: DniField, file: Blob, filename: string): Observable<DniUploadResult> {
    const formData = new FormData();
    formData.append('file', file, filename);
    const segment = field === 'dniFrontal' ? 'dni-frontal' : 'dni-reverso';
    return this.http
      .post<IResponse<DniUploadResult>>(`${this.url}/profile/${segment}`, formData)
      .pipe(map(response => response.data));
  }

  getDniSignedUrl(field: DniField): Observable<{ url: string }> {
    const segment = field === 'dniFrontal' ? 'dni-frontal' : 'dni-reverso';
    return this.http
      .get<IResponse<{ url: string }>>(`${this.url}/profile/${segment}/url`)
      .pipe(map(response => response.data));
  }

  checkDni(documentType: string, documentNumber: string): Observable<CheckDniResult> {
    return this.http
      .post<IResponse<CheckDniResult>>(`${this.url}/profile/check-dni`, {
        documentType,
        documentNumber,
      })
      .pipe(map(response => response.data));
  }
}
